import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Building2, Save, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/errors/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface CompanyInfo {
  company_name?: string;
  abn?: string;
  registered_address?: string;
  contact_email?: string;
  contact_phone?: string;
  website?: string;
}

interface CompanyInformationContainerProps {
  pageId: string;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  forceOpen?: boolean;
  workOrderEnabled?: boolean;
}

const CompanyInformationContainer: React.FC<CompanyInformationContainerProps> = ({ 
  pageId,
  isOpen,
  onToggle,
  forceOpen,
  workOrderEnabled = false 
}) => {
  const { toast } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>({
    company_name: '',
    abn: '',
    registered_address: '',
    contact_email: '',
    contact_phone: '',
    website: '',
  });

  // Sync with parent state if provided
  useEffect(() => {
    if (isOpen !== undefined) {
      setIsExpanded(isOpen);
    }
  }, [isOpen]);
  
  // Handle forceOpen prop - this overrides normal behavior temporarily
  useEffect(() => {
    if (forceOpen) {
      setIsExpanded(true);
    }
  }, [forceOpen]);
  
  // Load existing company information
  useEffect(() => {
    if (pageId) {
      loadCompanyInfo();
    }
  }, [pageId]);

  const loadCompanyInfo = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('workbook')
        .select('company_info')
        .eq('id', pageId)
        .single();

      if (error) throw error;
      
      if (data?.company_info) {
        const info = data.company_info;
        // Handle migration from old format (object) to new format (string)
        if (info.registered_address && typeof info.registered_address === 'object') {
          const addr = info.registered_address;
          const addressParts = [];
          if (addr.street) addressParts.push(addr.street);
          if (addr.city) addressParts.push(addr.city);
          if (addr.state) addressParts.push(addr.state);
          if (addr.postcode) addressParts.push(addr.postcode);
          if (addr.country) addressParts.push(addr.country);
          info.registered_address = addressParts.join(', ');
        }
        setCompanyInfo(info);
      }
    } catch (error) {
      console.error('Error loading company information:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setHasChanges(true);
    setCompanyInfo(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    // Basic validation
    if (!companyInfo.company_name?.trim()) {
      toast({
        title: 'Company Name Required',
        description: 'Please enter your company name',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      const updatedInfo = {
        ...companyInfo,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('workbook')
        .update({ company_info: updatedInfo })
        .eq('id', pageId);

      if (error) throw error;

      toast({
        title: 'Company Information Saved',
        description: 'Your company information has been updated successfully',
        variant: 'default',
      });
      
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving company information:', error);
      toast({
        title: 'Error',
        description: 'Failed to save company information',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isComplete = !!(
    companyInfo.company_name?.trim() &&
    companyInfo.abn?.trim() &&
    ((typeof companyInfo.registered_address === 'string' && companyInfo.registered_address?.trim()) ||
     (typeof companyInfo.registered_address === 'object' && companyInfo.registered_address?.street?.trim())) &&
    companyInfo.contact_email?.trim() &&
    companyInfo.contact_phone?.trim() &&
    companyInfo.website?.trim()
  );

  return (
    <div className="space-y-4">
      {/* Accordion Header */}
      <button
        onClick={() => {
          const newState = !isExpanded;
          if (onToggle) {
            onToggle(newState);
          } else {
            setIsExpanded(newState);
          }
        }}
        className={cn(
          "w-full flex items-center justify-between p-4 rounded-lg transition-all",
          "bg-white dark:bg-gray-800",
          "border border-gray-200 dark:border-gray-700",
          "hover:border-purple-300 dark:hover:border-purple-600",
          isExpanded && "border-purple-300 dark:border-purple-600 shadow-sm"
        )}
      >
        <div className="flex items-center gap-3">
          <div className={cn(
            "p-2 rounded-lg transition-colors",
            isComplete 
              ? "bg-blue-100 dark:bg-blue-900/30" 
              : "bg-gray-100 dark:bg-gray-900/30"
          )}>
            <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              Company Information
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {isComplete 
                ? `${companyInfo.company_name} • ABN: ${companyInfo.abn}`
                : 'Set up your company details for professional documents'
              }
            </p>
          </div>
        </div>
        <ChevronDown 
          className={cn(
            "h-5 w-5 text-gray-400 transition-transform",
            isExpanded && "rotate-180"
          )} 
        />
      </button>

      {/* Accordion Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 space-y-6">
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                  ))}
                </div>
              ) : (
                <>
                  {/* Company Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company_name">
                        Company Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="company_name"
                        value={companyInfo.company_name || ''}
                        onChange={(e) => handleInputChange('company_name', e.target.value)}
                        placeholder="ABC Consulting Pty Ltd"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="abn">
                        ABN (Australian Business Number) <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="abn"
                        value={companyInfo.abn || ''}
                        onChange={(e) => handleInputChange('abn', e.target.value)}
                        placeholder="12 345 678 901"
                      />
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                      Contact Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="contact_email">
                          Contact Email <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="contact_email"
                          type="email"
                          value={companyInfo.contact_email || ''}
                          onChange={(e) => handleInputChange('contact_email', e.target.value)}
                          placeholder="contact@company.com.au"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="contact_phone">
                          Contact Phone <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="contact_phone"
                          value={companyInfo.contact_phone || ''}
                          onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                          placeholder="+61 2 1234 5678"
                        />
                      </div>
                      
                      <div className="md:col-span-2 space-y-2">
                        <Label htmlFor="registered_address">
                          Registered Address <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="registered_address"
                          value={companyInfo.registered_address || ''}
                          onChange={(e) => handleInputChange('registered_address', e.target.value)}
                          placeholder="123 Business Street, Sydney NSW 2000, Australia"
                        />
                      </div>
                      
                      <div className="md:col-span-2 space-y-2">
                        <Label htmlFor="website">
                          Website <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="website"
                          type="url"
                          value={companyInfo.website || ''}
                          onChange={(e) => handleInputChange('website', e.target.value)}
                          placeholder="https://www.company.com.au"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      {!isComplete && workOrderEnabled && (
                        <>
                          <AlertCircle className="h-4 w-4 text-amber-500" />
                          <Link 
                            to={`/project/${pageId}/services`}
                            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline"
                          >
                            Complete all fields to create a Work Order
                          </Link>
                        </>
                      )}
                    </div>
                    <Button
                      onClick={handleSave}
                      disabled={isSaving || !hasChanges}
                      className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                    >
                      {isSaving ? (
                        <>
                          <Save className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CompanyInformationContainer;